package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.CreatedInvitationDto
import dev.lingvo.shared.InvitationDto
import dev.lingvo.shared.InvitationState
import dev.lingvo.shared.MemberDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.isBillable
import dev.lingvo.shared.seatEffect
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LCircleXIcon
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LOneTimeSecret
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LUserPlusIcon
import dev.lingvo.site.ds.LUsersIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.nav.label
import dev.lingvo.site.nav.labelInSentence
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.pages.app.projects.slug.settings.SettingsFormMeasure
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

/**
 * Who is in this workspace.
 *
 * A workspace surface, not a project one. It lived on a project's settings tab, which works while a
 * workspace has one project and misleads the moment it has two: the member list is the same list
 * whichever project you opened it from, and reaching it through one of them makes access look like
 * something a project owns. Someone deleting a project to tidy up should not have to wonder whether
 * their colleagues go with it.
 *
 * **Onboarding a team is one paste, not one invitation at a time.** The form took a single address,
 * so a five-person agency was five round trips through a modal that closed itself — and the fifth
 * one is the address that gets mistyped. Addresses now arrive one per line and each gets its own
 * outcome, because "three sent, one already a member, one refused" is the only receipt that tells
 * somebody what to do next.
 */
@Page("/app/settings/members")
@Layout(".components.layouts.AppLayout")
@Composable
fun WorkspaceMembersPage() {
    val toasts = rememberToasts()
    val scope = rememberCoroutineScope()
    // The workspace whose settings these are. With one workspace this is unambiguous; the switcher
    // that makes it selectable is a later step, and until then the strongest membership is the one
    // whose settings someone means.
    val ctx = rememberPageContext()
    val workspace = WorkspaceSelection.current()

    var reloadToken by remember { mutableStateOf(0) }
    var inviteOpen by remember { mutableStateOf(false) }
    var inviteAddresses by remember { mutableStateOf("") }
    var inviteRole by remember { mutableStateOf(Role.TRANSLATOR) }
    var invite by remember { mutableStateOf<InviteRun?>(null) }
    var resending by remember { mutableStateOf<String?>(null) }
    var copyFailure by remember { mutableStateOf<String?>(null) }
    var pendingRemoval by remember { mutableStateOf<MemberDto?>(null) }
    var pendingRole by remember { mutableStateOf<Pair<MemberDto, Role>?>(null) }
    var pendingWithdrawal by remember { mutableStateOf<InvitationDto?>(null) }
    var withdrawing by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var roleFilter by remember { mutableStateOf(emptySet<Role>()) }
    var selected by remember { mutableStateOf(emptySet<String>()) }
    var bulk by remember { mutableStateOf<BulkMemberRun?>(null) }
    var pendingBulkRemoval by remember { mutableStateOf(false) }
    var pendingBulkRole by remember { mutableStateOf<Role?>(null) }

    val slug = workspace?.slug
    // Retained: a resend, a removal or a role change reloads the list underneath what is on screen
    // rather than replacing forty rows with skeleton bars on the button somebody just pressed.
    val reloadable = loadReloadable(slug, reloadToken = reloadToken) {
        if (slug == null) null else ApiClient.workspaceMembers(slug)
    }
    val data = (reloadable.state as? Async.Success)?.value
    val usage = data?.usage
    val members = data?.members.orEmpty()
    val busy = bulk?.let { it.done < it.total } == true

    // Opening the panel puts the caret in the field it opened, so the press that asked for a form
    // does not then require a second press to use one.
    LaunchedEffect(inviteOpen) {
        if (inviteOpen) {
            (document.querySelector("#$INVITE_FIELD_ID textarea") as? HTMLElement)?.focus()
        }
    }

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
            PageHeader(
                t("app.settings.members.members", "Members"),
                tf(
                    "app.settings.members.everyone_who_can_reach_workspace",
                    "Everyone who can reach {workspace}, and what they may change.",
                    "workspace" to (workspace?.name ?: t("app.settings.members.this_workspace", "this workspace")),
                ),
                mark = { LUsersIcon() },
                meta = usage?.let {
                    {
                        // Stated, never typed: these numbers decide what the workspace pays.
                        //
                        // `if (n == 1) "seat" else "seats"` is English's rule under a general name, on
                        // the product that sells real plurals: Polish needs a third form at five and
                        // Japanese needs one, and neither was reachable from here.
                        //
                        // **One element per fact.** `PageHeader` gaps this row by `Space.LG`, but
                        // three adjacent `Text` nodes are one anonymous flex item, so the gap had
                        // nothing to sit between and the three facts read as "1 person1 billable
                        // seatFree". A `Span` each gives the row three items to space.
                        Span {
                            Text(
                                tPlural(
                                    "app.settings.members.count.person",
                                    it.totalMembers,
                                    "{count} person",
                                    "{count} people",
                                ),
                            )
                        }
                        Span {
                            Text(
                                tPlural(
                                    "app.settings.members.count.billable_seat",
                                    it.billableSeats,
                                    "{count} billable seat",
                                    "{count} billable seats",
                                ),
                            )
                        }
                        Span { Text(it.plan.displayName) }
                    }
                },
                actions = {
                    // Absent until the server has said this reader may invite. Offered while the list
                    // is still loading it would be a button whose only possible outcome is a 403.
                    //
                    // Absent again while the inline form is open — and it opens by itself on a
                    // one-member workspace. The form's own accent "Send invite" is the page's one
                    // primary; this button beside it was a second accent whose only remaining
                    // effect was to set a flag that is already set.
                    if (data?.canInvite == true && !inviteFormIsOpen(inviteOpen, members.size)) {
                        LButton(
                            t("app.settings.members.invite_people", "Invite people"),
                            { inviteOpen = true },
                            leadingIcon = { LUserPlusIcon() },
                        )
                    }
                },
            )
            reloadable.refreshError?.let {
                LAlert(
                    t("app.settings.members.that_refresh_didnt_land", "That refresh didn't land"),
                    t(
                        "app.settings.members.the_list_below_is_the_last",
                        "The list below is the last answer the server gave.",
                    ),
                    variant = LAlertVariant.WARNING,
                    dense = true,
                    live = true,
                )
            }
            LAsyncContent(
                reloadable.state,
                rows = 4,
                loadingLabel = t("app.settings.members.loading_members", "Loading members"),
                onRetry = { reloadToken++ },
                gap = Space.XXL,
            ) { loaded ->
                if (loaded == null) {
                    // Not a fault, and the Retry it offered re-derived the same null for ever: the load
                    // returns null only when this account is in no workspace at all. A red alert for a
                    // state that is nobody's mistake is what `LStates` was written to remove, and
                    // `/app` answers this state with the one action that fixes it.
                    LEmptyState(
                        t("app.settings.members.you_are_not_in_a_workspace", "You are not in a workspace yet"),
                        t("app.settings.members.a_workspace_holds_the_people_this", "A workspace holds the people this page is about. Create one, or ask " +
                            "whoever invited you to send the invitation again."),
                        Modifier.fillMaxWidth(),
                    ) {
                        LButton(
                            t("app.settings.members.create_a_workspace", "Create a workspace"),
                            { ctx.router.tryRoutingTo("/app/workspaces") },
                        )
                    }
                    return@LAsyncContent
                }

                if (loaded.canInvite) {
                    // The plan's seat cap, applied to the form and not only to the reply. The options
                    // offered ADMIN on every plan and the price line quoted "$0 a month more" on Free,
                    // whose cap is one seat the owner already holds — so the server refused the
                    // invitation after the click, as a red toast, on the one screen that could have
                    // said so beforehand. Same arithmetic as `capabilitiesFor`, and the sentence is
                    // `seatCapReason`, so the rule is stated in one place.
                    val seatLimit = loaded.usage?.plan?.maxBillableSeats
                    val seatAvailable = seatLimit == null || (loaded.usage?.billableSeats ?: 0) < seatLimit
                    val invitableRoles =
                        if (seatAvailable) INVITABLE_ROLES else INVITABLE_ROLES.filterNot { it.isBillable }
                    // A seat can fill while this form sits open. A select whose value is not among its
                    // options renders blank and submits a role the server will refuse, so the held
                    // choice is re-checked against what is still offered.
                    val selectedRole = if (inviteRole in invitableRoles) inviteRole else Role.TRANSLATOR
                    val addresses = parseInviteAddresses(inviteAddresses)
                    val sending = invite?.let { it.done < it.total } == true

                    if (inviteFormIsOpen(inviteOpen, loaded.members.size)) {
                        Column(
                            Modifier.fillMaxWidth().maxWidth(SettingsFormMeasure)
                                .gap(Space.SM).attr("id", INVITE_FIELD_ID),
                        ) {
                            Row(
                                Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
                                verticalAlignment = Alignment.Bottom,
                            ) {
                                LTextArea(
                                    inviteAddresses,
                                    { inviteAddresses = it },
                                    modifier = Modifier.flexGrow(1),
                                    label = t("app.settings.members.invite_by_email", "Invite by email"),
                                    placeholder = t(
                                        "app.settings.members.one_address_per_line",
                                        "One address per line",
                                    ),
                                    enabled = !sending,
                                )
                                LSelect(
                                    selectedRole.name,
                                    invitableRoles.map { LSelectOption(it.name, it.label()) },
                                    { value ->
                                        inviteRole = Role.entries.firstOrNull { it.name == value }
                                            ?: Role.TRANSLATOR
                                    },
                                    label = t("app.settings.members.role", "Role"),
                                )
                                LButton(
                                    // The real count, so nobody presses "Send invite" believing it
                                    // covers the six lines they pasted and finds it sent one.
                                    if (addresses.size <= 1) {
                                        t("app.settings.members.send_invite", "Send invite")
                                    } else {
                                        tf(
                                            "app.settings.members.send_count_invites",
                                            "Send {count} invites",
                                            "count" to addresses.size.toString(),
                                        )
                                    },
                                    {
                                        val target = slug ?: return@LButton
                                        invite = InviteRun(total = addresses.size)
                                        scope.launch {
                                            addresses.forEach { address ->
                                                val outcome = try {
                                                    InviteOutcome(
                                                        email = address,
                                                        created = ApiClient.inviteToWorkspace(
                                                            target,
                                                            address,
                                                            selectedRole,
                                                        ),
                                                        error = null,
                                                    )
                                                } catch (cancelled: CancellationException) {
                                                    // Navigating away cancels this job, and catching
                                                    // that as a failure reports a refusal over a screen
                                                    // that has already gone.
                                                    throw cancelled
                                                } catch (failure: Throwable) {
                                                    InviteOutcome(
                                                        email = address,
                                                        created = null,
                                                        error = failure.safeMessage(
                                                            tr(
                                                                "app.settings.members.couldnt_create_that_invitation",
                                                                "Couldn't create that invitation.",
                                                            ),
                                                        ),
                                                    )
                                                }
                                                invite = invite?.let { it.copy(results = it.results + outcome) }
                                            }
                                            inviteAddresses = ""
                                            copyFailure = null
                                            // The list is reloaded underneath; the panel of one-time
                                            // links stays exactly where it is, because a value shown
                                            // once must not be taken away by a refresh.
                                            reloadToken++
                                        }
                                    },
                                    enabled = addresses.isNotEmpty() && !sending,
                                    loading = sending,
                                    leadingIcon = { LUserPlusIcon() },
                                )
                            }
                            // Why the admin option is missing, said where it is missing from. Without
                            // this the select simply has one fewer entry and nothing accounts for it.
                            if (!seatAvailable && loaded.usage != null) {
                                SeatLimitNotice(loaded.usage!!.plan, workspace?.role)
                            } else if (selectedRole.isBillable) {
                                // `seatEffect` exists because "the money has to be stated before the
                                // click" — but only the role-change modal was saying it. Inviting an
                                // admin adds exactly the same billable seat, and this form charged for
                                // it in silence, the invoice-surprise that rule was written against.
                                val plan = loaded.usage?.plan
                                LAlert(
                                    title = tPlural(
                                        "app.settings.members.count.this_invitation_adds_a_billable_seat",
                                        addresses.size.coerceAtLeast(1),
                                        "This invitation adds a billable seat",
                                        "These invitations add {count} billable seats",
                                    ),
                                    description = plan?.let {
                                        tf(
                                            "app.settings.members.accepting_it_bills_one_more_seat",
                                            "Each acceptance bills one more seat: {price} a month more, " +
                                                "charged pro rata for the rest of the period. Translator " +
                                                "and viewer seats are free.",
                                            "price" to "$${it.pricePerSeat}",
                                        )
                                    } ?: t(
                                        "app.settings.members.owner_and_admin_seats_are_billable",
                                        "Owner and admin seats are billable; translator and viewer " +
                                            "seats are free on every plan.",
                                    ),
                                    variant = LAlertVariant.WARNING,
                                )
                            }
                            LMuted(
                                t("app.settings.members.an_owner_can_only_be_appointed", "An owner can only be appointed after someone joins — invitations " +
                                    "never grant ownership."),
                            )
                        }
                    }

                    // **The links, printed.** This panel used to say "Send this link to …" and "This is
                    // the only time the link is shown", and then show no link at all — the token was on
                    // the DTO and read nowhere. The only button dismissed it, and only a hash is
                    // stored, so the invitation became undeliverable, unrevokable and un-reissuable in
                    // one click: the pending row blocks that address until somebody revokes it, and
                    // expiry never lifts the block.
                    invite?.let { run ->
                        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                            LTaskProgress(
                                done = run.done,
                                total = run.total,
                                failed = run.failed,
                                label = t("app.settings.members.sending_invitations", "Sending invitations"),
                            )
                            run.results.forEach { outcome ->
                                val created = outcome.created
                                if (created == null) {
                                    Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                                        LCircleXIcon()
                                        Text(outcome.email)
                                        LMuted(outcome.error.orEmpty())
                                    }
                                } else {
                                    Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                                        LAlert(
                                            // The address is interpolated rather than written into the
                                            // translatable sentence: a customer's email is not copy for
                                            // a translator to handle.
                                            tf(
                                                "app.settings.members.send_this_link_to_email",
                                                "Send this link to {email}",
                                                "email" to created.invitation.email,
                                            ),
                                            issuedInvitationNotice(run.replacedAnEarlierLink),
                                            variant = LAlertVariant.WARNING,
                                            dense = true,
                                        )
                                        // The component every other one-time credential in the product
                                        // uses. A bare `<pre>` establishes a scroller no keyboard can
                                        // reach and carries no copy button, over a value only a hash of
                                        // which is stored.
                                        LOneTimeSecret(
                                            value = "${window.location.origin}/invite?token=${created.token}",
                                            label = tr("app.settings.members.invitation_link", "Invitation link"),
                                            copyLabel = t("app.settings.members.copy_link", "Copy link"),
                                            onCopied = { copyFailure = null },
                                            onCopyFailed = {
                                                copyFailure = tr(
                                                    "app.settings.members.couldnt_reach_the_clipboard_select_the",
                                                    "Couldn't reach the clipboard. Select the link above " +
                                                        "and copy it manually before leaving.",
                                                )
                                            },
                                        )
                                    }
                                }
                            }
                            if (run.done >= run.total) {
                                LButton(
                                    t("app.settings.members.i_have_sent_them", "I have sent them"),
                                    { invite = null; inviteOpen = false; reloadToken++ },
                                    variant = LButtonVariant.GHOST,
                                    leadingIcon = { LCheckIcon() },
                                )
                            }
                            // Reached only by pressing copy and having it refused, so it is announced.
                            copyFailure?.let {
                                LAlert(
                                    t("app.settings.members.copy_it_by_hand", "Copy it by hand"),
                                    it,
                                    variant = LAlertVariant.WARNING,
                                    live = true,
                                )
                            }
                        }
                    }
                } else {
                    // A translator or viewer saw the member list, no invite form, no pending section
                    // and an empty Action column, with nothing anywhere saying why — the page read as
                    // broken rather than as read-only. Every sibling settings surface answers this with
                    // a titled INFO alert naming who does hold the privilege.
                    LAlert(
                        title = t(
                            "app.settings.members.membership_is_managed_by_an_owner",
                            "Membership is managed by an owner or admin"
                        ),
                        // One literal, not a `+` run. `extract-strings.mjs` reads a wrapped call site
                        // as a key followed by a single quoted argument and a closing paren; a
                        // concatenation inside the parentheses matches neither that pass nor the split
                        // pass that joins a run trailing OUTSIDE the call.
                        description = t(
                            "app.settings.members.inviting_people_withdrawing_invitations",
                            "Inviting people, withdrawing invitations, changing roles and removing members belong to the people who manage this workspace. You can see who is here."
                        ),
                        variant = LAlertVariant.INFO,
                    )
                }

                bulk?.let { run ->
                    // The receipt: what the batch did, per person, including the ones it could not do.
                    Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                        LTaskProgress(
                            done = run.done,
                            total = run.total,
                            failed = run.failed,
                            label = t("app.settings.members.applying_the_selection", "Applying the selection"),
                        )
                        run.results.forEach { outcome ->
                            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                                if (outcome.error == null) LCheckIcon() else LCircleXIcon()
                                Text(outcome.name)
                                outcome.error?.let { LMuted(it) }
                            }
                        }
                        if (run.done >= run.total) {
                            LButton(
                                t("app.settings.members.hide_this_receipt", "Hide this receipt"),
                                { bulk = null },
                                variant = LButtonVariant.GHOST,
                                size = LButtonSize.SM,
                            )
                        }
                    }
                }

                MembersRoster(
                    members = loaded.members,
                    query = query,
                    onQuery = { query = it },
                    roleFilter = roleFilter,
                    onRoleFilter = { roleFilter = it },
                    selected = selected.filter { id -> loaded.members.any { it.userId == id } }.toSet(),
                    onSelected = { selected = it },
                    onRemove = { pendingRemoval = it },
                    onRoleChange = { member, next -> pendingRole = member to next },
                    onToggleApprove = { member, next ->
                        scope.launch {
                            settingsOperationResult {
                                ApiClient.setWorkspaceMemberReviewer(slug!!, member.userId, next)
                            }.fold(
                                { reloadToken++ },
                                { failure ->
                                    toasts.show(
                                        failure.safeMessage(
                                            tr("app.settings.members.couldnt_change_that", "Couldn't change that."),
                                        ),
                                        LToastVariant.DANGER,
                                        TOAST_UNTIL_DISMISSED,
                                    )
                                    reloadToken++
                                },
                            )
                        }
                    },
                    onBulkRemove = { pendingBulkRemoval = true },
                    onBulkRole = { pendingBulkRole = it },
                    busy = busy,
                )

                val pending = loaded.invitations.filter { it.state == InvitationState.PENDING }
                if (loaded.canInvite && pending.isNotEmpty()) {
                    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                        // Said before the click, not after it. A resend rotates the token, so the
                        // message the invitee is looking at stops working the moment somebody presses
                        // it "just to be safe" — which the notice on the new link only reveals once the
                        // old one is already dead.
                        LMuted(t("app.settings.members.resending_issues_a_new_link", "Resending issues a new link and stops the earlier one working."))
                        LTable(caption = t("app.settings.members.pending_invitations", "Pending invitations")) {
                            LTableHead {
                                LTableRow {
                                    LTableHeaderCell { Text(t("app.settings.members.email", "Email")) }
                                    LTableHeaderCell { Text(t("app.settings.members.role", "Role")) }
                                    LTableHeaderCell { Text(t("app.settings.members.expires", "Expires")) }
                                    LTableHeaderCell { Text("") }
                                }
                            }
                            LTableBody {
                                pending.forEach { invitation ->
                                    LTableRow {
                                        LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                                            Text(invitation.email)
                                        }
                                        LTableCell { LBadge(invitation.role.label()) }
                                        LTableCell { Text(invitation.expiresAt.take(10)) }
                                        // **The only way back from a typo.** The pending row holds that
                                        // address against a partial unique index that clears on accept
                                        // or revoke and never on expiry — so without this, inviting the
                                        // right person at a mistyped address is refused permanently,
                                        // for an invitation whose link nobody can reproduce.
                                        LTableCell {
                                            Row(Modifier.gap(Space.XS)) {
                                                LButton(
                                                    t("app.settings.members.resend", "Resend"),
                                                    {
                                                        scope.launch {
                                                            resending = invitation.id
                                                            // `runCatching` caught the
                                                            // CancellationException of leaving this page
                                                            // and reported it as a failed resend, for a
                                                            // link that was in fact rotated.
                                                            settingsOperationResult {
                                                                ApiClient.resendWorkspaceInvitation(slug!!, invitation.id)
                                                            }.fold(
                                                                { result ->
                                                                    invite = InviteRun(
                                                                        total = 1,
                                                                        replacedAnEarlierLink = true,
                                                                        results = listOf(
                                                                            InviteOutcome(
                                                                                email = invitation.email,
                                                                                created = result,
                                                                                error = null,
                                                                            ),
                                                                        ),
                                                                    )
                                                                    copyFailure = null
                                                                    toasts.show(
                                                                        trf(
                                                                            "app.settings.members.new_link_sent_to_email",
                                                                            "New link sent to {email}",
                                                                            "email" to invitation.email,
                                                                        ),
                                                                        LToastVariant.SUCCESS,
                                                                    )
                                                                },
                                                                { error ->
                                                                    toasts.show(
                                                                        error.safeMessage(
                                                                            tr(
                                                                                "app.settings.members.couldnt_resend_that_invitation",
                                                                                "Couldn't resend that invitation.",
                                                                            ),
                                                                        ),
                                                                        LToastVariant.DANGER,
                                                                        TOAST_UNTIL_DISMISSED,
                                                                    )
                                                                },
                                                            )
                                                            resending = null
                                                        }
                                                    },
                                                    variant = LButtonVariant.GHOST,
                                                    size = LButtonSize.SM,
                                                    enabled = resending == null && withdrawing == null,
                                                    loading = resending == invitation.id,
                                                    // Names the row and states the cost, because a
                                                    // screen reader on the fourth "Resend" of a list
                                                    // hears neither.
                                                    accessibleLabel = tf(
                                                        "app.settings.members.resend_the_invitation_to_email",
                                                        "Resend the invitation to {email} — the earlier link stops working"
                                                    , "email" to invitation.email),
                                                )
                                                LButton(
                                                    t("app.settings.members.withdraw", "Withdraw"),
                                                    // Asks first. This destroys a live invitation whose
                                                    // link nobody can reproduce, and it sat one
                                                    // unguarded click from a "Resend" that only
                                                    // re-sends one — the same two words apart, with
                                                    // only one of them undoable.
                                                    { pendingWithdrawal = invitation },
                                                    variant = LButtonVariant.GHOST,
                                                    size = LButtonSize.SM,
                                                    enabled = resending == null && withdrawing == null,
                                                    loading = withdrawing == invitation.id,
                                                    accessibleLabel = tf(
                                                        "app.settings.members.withdraw_the_invitation_to_email",
                                                        "Withdraw the invitation to {email}"
                                                    , "email" to invitation.email),
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    val removal = pendingRemoval
    // Removing a billable member frees exactly the seat the role modal prices when it demotes one,
    // and only the demotion was saying so — the same invoice moving, announced on one of the two
    // screens that move it.
    val removalPlan = usage?.plan
    val removalSeatNote = if (removal != null && removal.role.isBillable && removalPlan != null) {
        " " + tf(
            "app.settings.members.this_also_frees_a_billable_seat",
            "This also frees a billable seat, so the bill drops by {price} a month from the next invoice. Nothing is refunded today.",
            "price" to "$${removalPlan.pricePerSeat}",
        )
    } else {
        ""
    }
    LConfirmDialog(
        open = removal != null,
        title = tf("app.settings.members.remove_name", "Remove {name}?", "name" to removal?.name.orEmpty()),
        confirmLabel = t("app.settings.members.remove", "Remove"),
        onConfirm = {
            val member = pendingRemoval ?: return@LConfirmDialog
            pendingRemoval = null
            scope.launch {
                settingsOperationResult {
                    ApiClient.removeWorkspaceMember(slug!!, member.userId)
                }.fold(
                    {
                        toasts.show(
                            trf(
                                "app.settings.members.member_removed",
                                "{member} removed",
                                "member" to member.name,
                            ),
                            LToastVariant.SUCCESS,
                            4_000,
                        )
                        reloadToken++
                    },
                    { failure ->
                        toasts.show(
                            failure.safeMessage(
                                tr(
                                    "app.settings.members.couldnt_remove_that_member",
                                    "Couldn't remove that member.",
                                ),
                            ),
                            LToastVariant.DANGER,
                            TOAST_UNTIL_DISMISSED,
                        )
                    },
                )
            }
        },
        onCancel = { pendingRemoval = null },
        consequenceTitle = t("app.settings.members.they_lose_access_immediately", "They lose access immediately"),
        consequence = tf(
            "app.settings.members.name_will_no_longer_see_this_workspace",
            "{name} will no longer see this workspace. Their past translations stay.",
            "name" to removal?.name.orEmpty(),
        ) + removalSeatNote,
    )

    // The bulk equivalent of the same question, with the exact scope in the title and the money in
    // the consequence — a batch that says "Remove selected?" is a batch nobody can audit afterwards.
    val bulkTargets = members.filter { it.userId in selected }
    LConfirmDialog(
        open = pendingBulkRemoval,
        title = tPlural(
            "app.settings.members.count.remove_person_question",
            bulkTargets.size,
            "Remove {count} person?",
            "Remove {count} people?",
        ),
        confirmLabel = t("app.settings.members.remove", "Remove"),
        onConfirm = {
            pendingBulkRemoval = false
            val targets = bulkTargets
            bulk = BulkMemberRun(total = targets.size)
            scope.launch {
                targets.forEach { member ->
                    val outcome = settingsOperationResult {
                        ApiClient.removeWorkspaceMember(slug!!, member.userId)
                    }.fold(
                        { BulkMemberOutcome(member.name, null) },
                        { failure ->
                            BulkMemberOutcome(
                                member.name,
                                failure.safeMessage(
                                    tr(
                                        "app.settings.members.couldnt_remove_that_member",
                                        "Couldn't remove that member.",
                                    ),
                                ),
                            )
                        },
                    )
                    bulk = bulk?.let { it.copy(results = it.results + outcome) }
                }
                selected = emptySet()
                reloadToken++
            }
        },
        onCancel = { pendingBulkRemoval = false },
        consequenceTitle = t("app.settings.members.they_lose_access_immediately", "They lose access immediately"),
        consequence = tPlural(
            "app.settings.members.count.they_will_no_longer_see_this",
            bulkTargets.count { it.role.isBillable },
            "They will no longer see this workspace, and {count} billable seat is freed from the next invoice. Their past translations stay.",
            "They will no longer see this workspace, and {count} billable seats are freed from the next invoice. Their past translations stay.",
        ),
    )

    val bulkRole = pendingBulkRole
    LConfirmDialog(
        open = bulkRole != null,
        title = bulkRole?.let {
            trf(
                "app.settings.members.make_count_people_role",
                "Make {count} people {role}?",
                "count" to bulkTargets.size.toString(),
                "role" to it.labelInSentence(),
            )
        }.orEmpty(),
        confirmLabel = t("app.settings.members.change_role", "Change role"),
        onConfirm = {
            val next = pendingBulkRole ?: return@LConfirmDialog
            pendingBulkRole = null
            val targets = bulkTargets
            bulk = BulkMemberRun(total = targets.size)
            scope.launch {
                targets.forEach { member ->
                    val outcome = settingsOperationResult {
                        ApiClient.updateWorkspaceMemberRole(
                            slug!!,
                            member.userId,
                            next,
                            usage?.billableSeats,
                        )
                    }.fold(
                        { BulkMemberOutcome(member.name, null) },
                        { failure ->
                            BulkMemberOutcome(
                                member.name,
                                failure.safeMessage(
                                    tr(
                                        "app.settings.members.couldnt_change_that_role",
                                        "Couldn't change that role.",
                                    ),
                                ),
                            )
                        },
                    )
                    bulk = bulk?.let { it.copy(results = it.results + outcome) }
                }
                selected = emptySet()
                reloadToken++
            }
        },
        onCancel = { pendingBulkRole = null },
        consequenceTitle = t("app.settings.members.the_bill_moves", "The bill moves"),
        consequence = bulkRole?.let { next ->
            val delta = bulkTargets.sumOf { seatEffect(it.role, next) }
            val plan = usage?.plan
            when {
                delta > 0 && plan != null -> trf(
                    "app.settings.members.this_adds_count_billable_seats_at",
                    "This adds {count} billable seats at {price} a month each, charged pro rata for the rest of this period.",
                    "count" to delta.toString(),
                    "price" to "$${plan.pricePerSeat}",
                )
                delta < 0 && plan != null -> trf(
                    "app.settings.members.this_frees_count_billable_seats_so",
                    "This frees {count} billable seats, so the bill drops from the next invoice. Nothing is refunded today.",
                    "count" to (-delta).toString(),
                )
                else -> tr(
                    "app.settings.members.no_change_to_the_bill_sentence",
                    "Translator and viewer seats are free and unlimited, so nothing about the bill changes.",
                )
            }
        },
    )

    // **Never one click.** Withdrawing destroys a live invitation, and only a hash of its link is
    // stored — so a mis-click refuses that address until somebody notices, for a link nobody can
    // reproduce. It sat beside a "Resend" that is undoable, styled identically.
    val withdrawal = pendingWithdrawal
    LConfirmDialog(
        open = withdrawal != null,
        title = tf(
            "app.settings.members.withdraw_the_invitation_to_email_2",
            "Withdraw the invitation to {email}?",
            "email" to withdrawal?.email.orEmpty(),
        ),
        confirmLabel = t("app.settings.members.withdraw", "Withdraw"),
        onConfirm = {
            val invitation = pendingWithdrawal ?: return@LConfirmDialog
            pendingWithdrawal = null
            scope.launch {
                withdrawing = invitation.id
                settingsOperationResult {
                    ApiClient.revokeWorkspaceInvitation(slug!!, invitation.id)
                }.fold(
                    {
                        toasts.show(tr("app.settings.members.invitation_withdrawn", "Invitation withdrawn"), LToastVariant.SUCCESS)
                        reloadToken++
                    },
                    {
                        toasts.show(
                            it.safeMessage(
                                tr(
                                    "app.settings.members.couldnt_withdraw_that_invitation",
                                    "Couldn't withdraw that invitation.",
                                ),
                            ),
                            LToastVariant.DANGER,
                            TOAST_UNTIL_DISMISSED,
                        )
                    },
                )
                withdrawing = null
            }
        },
        onCancel = { pendingWithdrawal = null },
        consequenceTitle = t("app.settings.members.the_link_you_sent_stops_working", "The link you already sent stops working"),
        consequence = tf(
            "app.settings.members.email_can_no_longer_use_the_invitation",
            "{email} can no longer use the invitation, and the link cannot be shown again — only a hash of it is stored. You can invite that address afresh afterwards.",
            "email" to withdrawal?.email.orEmpty(),
        ),
    )

    val roleChange = pendingRole
    LModal(
        roleChange != null,
        roleChange?.let { (member, next) ->
            trf(
                "app.settings.members.make_member_role",
                "Make {member} {role}?",
                "member" to member.name,
                "role" to next.labelInSentence(),
            )
        }.orEmpty(),
        { pendingRole = null },
        actions = {
            LButton(t("app.settings.members.cancel", "Cancel"), { pendingRole = null }, variant = LButtonVariant.GHOST)
            LButton(t("app.settings.members.change_role", "Change role"), {
                val (member, next) = pendingRole ?: return@LButton
                pendingRole = null
                scope.launch {
                    settingsOperationResult {
                        ApiClient.updateWorkspaceMemberRole(slug!!, member.userId, next, usage?.billableSeats)
                    }.fold(
                        {
                            toasts.show(
                                trf(
                                    "app.settings.members.member_is_now_role",
                                    "{member} is now {role}",
                                    "member" to member.name,
                                    "role" to next.labelInSentence(),
                                ),
                                LToastVariant.SUCCESS,
                                4_000,
                            )
                            reloadToken++
                        },
                        { failure ->
                            toasts.show(
                                failure.safeMessage(
                                    tr(
                                        "app.settings.members.couldnt_change_that_role",
                                        "Couldn't change that role.",
                                    ),
                                ),
                                LToastVariant.DANGER,
                                TOAST_UNTIL_DISMISSED,
                            )
                            reloadToken++
                        },
                    )
                }
            }, leadingIcon = { LUsersIcon() })
        },
    ) {
        if (roleChange != null) {
            val (member, next) = roleChange
            val plan = usage?.plan
            val effect = seatEffect(member.role, next)
            when {
                effect > 0 && plan != null -> LAlert(
                    t("app.settings.members.this_adds_a_billable_seat", "This adds a billable seat"),
                    tf(
                        "app.settings.members.member_becomes_role_billing_one_more_seat",
                        "{member} becomes {role}, so this workspace bills one more seat: {price} a " +
                            "month more, charged pro rata for the rest of this period.",
                        "member" to member.name,
                        "role" to next.labelInSentence(),
                        "price" to "$${plan.pricePerSeat}",
                    ),
                    variant = LAlertVariant.WARNING,
                )
                effect < 0 && plan != null -> LAlert(
                    t("app.settings.members.this_removes_a_billable_seat", "This removes a billable seat"),
                    tf(
                        "app.settings.members.member_becomes_role_bill_drops",
                        "{member} becomes {role}. Translator and viewer seats are free, so the bill " +
                            "drops by {price} a month from the next invoice. Nothing is refunded today.",
                        "member" to member.name,
                        "role" to next.labelInSentence(),
                        "price" to "$${plan.pricePerSeat}",
                    ),
                    variant = LAlertVariant.INFO,
                )
                else -> LAlert(
                    t("app.settings.members.no_change_to_the_bill", "No change to the bill"),
                    t("app.settings.members.translator_and_viewer_seats_are_free", "Translator and viewer seats are free and unlimited on every plan."),
                    variant = LAlertVariant.INFO,
                )
            }
        }
    }

    ToastHost(toasts)
}

/** The element the "Invite people" action puts the caret into. */
private const val INVITE_FIELD_ID = "members-invite-form"

private val INVITABLE_ROLES = listOf(Role.ADMIN, Role.TRANSLATOR, Role.VIEWER)

/**
 * Whether the inline invite form is on screen — asked in one place by both things that need it.
 *
 * A workspace with one person in it opens the form by itself, because inviting somebody is the
 * only thing there is to do on that page. The header's "Invite people" is **absent** whenever this
 * is true: the form's own accent "Send invite" is then the page's one primary, and a second accent
 * button above it whose only effect was to set a flag that is already set is two primaries and a
 * control that does nothing. The two call sites had written the condition out separately, which is
 * how they came to disagree.
 */
internal fun inviteFormIsOpen(inviteOpen: Boolean, memberCount: Int): Boolean =
    inviteOpen || memberCount <= 1

/**
 * One address per line, trimmed, de-duplicated, order preserved.
 *
 * Commas and semicolons are accepted too, because that is what comes out of a mail client's To
 * field and refusing it would make the paste this form exists for the one paste it cannot take. A
 * pure function, so "six lines, one of them blank, two of them the same address" is a test rather
 * than a thing somebody discovers with six real invitations.
 */
internal fun parseInviteAddresses(raw: String): List<String> =
    raw.split('\n', ',', ';')
        .map { it.trim() }
        .filter { it.isNotEmpty() }
        .distinct()

/** One "send these invitations" run: how far it got, and what each address answered. */
internal data class InviteRun(
    val total: Int,
    val replacedAnEarlierLink: Boolean = false,
    val results: List<InviteOutcome> = emptyList(),
) {
    val done: Int get() = results.size
    val failed: Int get() = results.count { it.created == null }
}

internal data class InviteOutcome(
    val email: String,
    val created: CreatedInvitationDto?,
    val error: String?,
)

/** One bulk role change or removal: the same shape, so both receipts read the same way. */
internal data class BulkMemberRun(
    val total: Int,
    val results: List<BulkMemberOutcome> = emptyList(),
) {
    val done: Int get() = results.size
    val failed: Int get() = results.count { it.error != null }
}

internal data class BulkMemberOutcome(val name: String, val error: String?)

/**
 * The server's sentence, or this page's own — never an internal one.
 *
 * Cast first, the way `Keys.kt` and `Glossary.kt` do: a `check`/`require` failure inside a
 * mutation lambda is a `Throwable` with a message written for a developer, and the unguarded form
 * put "Required value was null." in front of the reader.
 */
private fun Throwable.safeMessage(fallback: String): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank) ?: fallback

internal fun issuedInvitationNotice(replacedAnEarlierLink: Boolean): String = buildString {
    append("This is the only time it is shown — Lingvo stores only a hash. ")
    if (replacedAnEarlierLink) append("The link from the earlier message has stopped working, so send this one. ")
    append("If mail is configured they also received it by email.")
}
